import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Button, Fab, Stack, Typography } from '@mui/material'
import { com } from '@eidu/web'
import { Add } from '@mui/icons-material'
import useEntityTypes from '../../io/useEntityTypes'
import useEntityType from '../../io/useEntityType'
import TanStackDataGrid from '../../components/data/TanStackDataGrid'
import EntityTypeNotAccessibleError from './EntityTypeNotAccessibleError'
import { useImpersonation } from '../../io/context/ImpersonationContext'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import entityTypeIdFromString = com.eidu.sharedlib.entity.type.entityTypeIdFromString

type ListPageContentProps = {
  onImport: () => void
  onNew: () => void
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
  pageSize: number
}

export const ListPageContent = ({ onImport, onNew, entityType, entityTypes, pageSize }: ListPageContentProps) => {
  const navigate = useNavigate()
  const { isReadOnly } = useImpersonation()
  const writesAllowed = !isReadOnly

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        p: 3,
        overflow: 'hidden',
      }}
    >
      {writesAllowed && (
        <Fab
          data-testid="entity-list-create-button"
          color="primary"
          aria-label={`Create ${entityType.name}`}
          onClick={onNew}
          sx={{
            position: 'fixed',
            bottom: (theme) => theme.spacing(2),
            right: (theme) => theme.spacing(2),
          }}
        >
          <Add />
        </Fab>
      )}
      {entityType && (
        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ flexShrink: 0 }}>
          <Typography variant="h4" data-testid="entity-list-title">
            {entityType.name}
          </Typography>
          {writesAllowed && (
            <Stack direction="row" justifyContent="flex-end">
              <Box>
                <Button data-testid="entity-list-import-button" color="primary" onClick={onImport}>
                  Import
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      )}
      {entityType && (
        <Box sx={{ flex: 1, overflow: 'hidden', pt: 1 }}>
          <TanStackDataGrid navigate={navigate} entityTypes={entityTypes} entityType={entityType} pageSize={pageSize} />
        </Box>
      )}
    </Box>
  )
}

export type ReadyListPageProps = {
  entityType: EntityType
  entityTypes: ReadonlyMap<EntityTypeId, EntityType>
}

const ReadyListPage = ({ entityType, entityTypes }: ReadyListPageProps) => {
  const navigate = useNavigate()

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <ListPageContent
        onImport={() => {
          navigate('import')
        }}
        onNew={() => {
          navigate('new')
        }}
        entityType={entityType}
        entityTypes={entityTypes}
        pageSize={50}
      />
    </Stack>
  )
}

export type ListPageProps = {
  entityTypeId: EntityTypeId
}

const ListPage = ({ entityTypeId }: ListPageProps) => {
  const { entityTypes, isLoading: typesLoading, error: typesError } = useEntityTypes()
  const { entityType, isLoading: typeLoading, error: typeError } = useEntityType(entityTypeId)

  const error = typesError ?? typeError
  const loading = typesLoading || typeLoading
  const loadingOverlayOpen = loading && !error

  return (
    <>
      {entityType && entityTypes && (
        <ReadyListPage key={entityType.id.toString()} entityType={entityType} entityTypes={entityTypes} />
      )}
      {(!entityType || !entityTypes) && !loadingOverlayOpen && <EntityTypeNotAccessibleError />}
    </>
  )
}

const ListPageRoute = () => {
  const params = useParams()

  if (params.entityTypeId) return <ListPage entityTypeId={entityTypeIdFromString(params.entityTypeId)} />
  else return <Typography sx={{ padding: 3 }}>Unknown state</Typography>
}

export default ListPageRoute
